import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/contexts/auth'
import { invalidateLibraryQueries } from '@/hooks/books/useBooks'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'
import type { TravelingBook } from '@/types'

const KEY = 'traveling-books'

export function useTravelingBooks(clubId: string | undefined) {
  const { user } = useAuth()

  return useQuery({
    queryKey: [KEY, clubId],
    queryFn: async (): Promise<TravelingBook[]> => {
      const { data, error } = await supabase
        .from('traveling_books')
        .select('*, stops:traveling_book_stops(*)')
        .eq('club_id', clubId)
        .order('created_at', { ascending: false })
      if (error) {
        captureError(error, { context: 'useTravelingBooks', clubId })
        throw error
      }
      return (data || []).map((trip) => ({
        ...trip,
        stops: [...(trip.stops || [])].sort((a, b) => a.position - b.position),
      }))
    },
    enabled: !!user?.id && !!clubId,
  })
}

/** Loans and libraries change with every hop, so refresh them too */
function useInvalidateTrips() {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  return (clubId: string) => {
    queryClient.invalidateQueries({ queryKey: [KEY, clubId] })
    invalidateLibraryQueries(queryClient, user?.id)
  }
}

export function useCreateTravelingBook() {
  const invalidate = useInvalidateTrips()

  return useMutation({
    mutationFn: async (vars: {
      clubId: string
      bookId: string
      route: string[]
      note?: string
    }) => {
      const { data, error } = await supabase.rpc('create_traveling_book', {
        p_club_id: vars.clubId,
        p_book_id: vars.bookId,
        p_route: vars.route,
        p_note: vars.note || null,
      })
      if (error) {
        captureError(error, { context: 'useCreateTravelingBook', clubId: vars.clubId })
        throw error
      }
      return data as string
    },
    onSuccess: (_id, vars) => invalidate(vars.clubId),
  })
}

export function usePassTravelingBook() {
  const invalidate = useInvalidateTrips()

  return useMutation({
    mutationFn: async (vars: { trip: TravelingBook; note?: string }) => {
      const { error } = await supabase.rpc('pass_traveling_book', {
        p_traveling_book_id: vars.trip.id,
        p_note: vars.note || null,
      })
      if (error) {
        captureError(error, { context: 'usePassTravelingBook', tripId: vars.trip.id })
        throw error
      }
    },
    onSuccess: (_data, vars) => invalidate(vars.trip.club_id),
  })
}

/** Stop currently holding the book (received, not yet passed) */
export function currentStop(trip: TravelingBook) {
  return trip.stops.find((s) => s.received_on && !s.passed_on) ?? null
}

export function nextStop(trip: TravelingBook) {
  const current = currentStop(trip)
  return (
    trip.stops.find((s) => !s.received_on && (!current || s.position > current.position)) ?? null
  )
}
